import { useEffect } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import useUserSession from "zustand-store/user";
import { getUserByCogId } from "api/user";

// Routes where the onboarding gate must NOT run — auth screens (the user may be
// mid sign-up / not yet a DB user) and the onboarding screens themselves
// (excluding these prevents a redirect loop into /setup-profile).
const GATE_EXCLUDED_ROUTES = [
  "/login",
  "/register",
  "/forgot-password",
  "/setup-profile",
  "/onboarding-audio-check",
];

/**
 * Single, app-level gate that keeps a half-onboarded user inside the onboarding
 * flow. "Onboarding complete" is inferred from a non-empty `profession` on the
 * DB user (the lighter heuristic — no schema column). Behaviour:
 *   - Only runs for authenticated users on a non-excluded route.
 *   - Reuses the shared "userProfile" React Query cache (same key as the profile
 *     dropdown) so it adds no extra network round-trips on a normal load.
 *   - Fails open: if the profile can't be read, it does nothing (never blocks
 *     access on a transient error).
 */
const OnboardingRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useUserSession();

  const enabled =
    isAuthenticated === true &&
    !GATE_EXCLUDED_ROUTES.includes(location.pathname);

  const { data: user } = useQuery("userProfile", getUserByCogId, {
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!enabled || !user) return;
    const professionMissing =
      !user.profession || String(user.profession).trim() === "";
    if (professionMissing) {
      navigate("/setup-profile", { replace: true });
    }
  }, [enabled, user, navigate]);

  return null;
};

export default OnboardingRedirect;
